import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '@src/store/base-query';
import { formatCardExpiration } from '@src/utils/payment-method-utils';

export const stripeApi = createApi({
  reducerPath: 'stripeQueries',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['PaymentMethods', 'StripeCustomer', 'Invoices'],
  endpoints: (builder) => ({
    // Setup Intent for adding payment methods
    createSetupIntent: builder.mutation({
      query: () => ({
        url: 'stripe/setup/create-intent',
        method: 'POST',
      }),
      invalidatesTags: ['StripeCustomer'],
    }),

    // Confirm Setup Intent and save payment method
    confirmSetupIntent: builder.mutation({
      query: (setupIntentId) => ({
        url: 'stripe/setup/confirm',
        method: 'POST',
        body: { setupIntentId },
      }),
      invalidatesTags: ['PaymentMethods'],
    }),

    // Get all payment methods
    getPaymentMethods: builder.query({
      query: () => 'stripe/payment-methods',
      providesTags: ['PaymentMethods'],
      transformResponse: (response) => {
        const data = response.data || [];

        // Transform each payment method to include formatted fields
        return data.map((pm) => ({
          ...pm,
          // Format expiration date from card.expMonth and card.expYear
          expirationDate: formatCardExpiration(pm.card?.expMonth, pm.card?.expYear),
          // Extract cardholder name from billing details
          cardholderName: pm.billingDetails?.name || 'N/A',
        }));
      },
    }),

    // Get default payment method
    getDefaultPaymentMethod: builder.query({
      query: () => 'stripe/payment-methods/default',
      providesTags: ['PaymentMethods'],
      transformResponse: (response) => {
        const pm = response.data;
        if (!pm) return null;

        return {
          ...pm,
          expirationDate: formatCardExpiration(pm.card?.expMonth, pm.card?.expYear),
          cardholderName: pm.billingDetails?.name || 'N/A',
        };
      },
    }),

    // Get a specific payment method
    getPaymentMethodById: builder.query({
      query: (id) => `stripe/payment-methods/${id}`,
      providesTags: (result, error, id) => [{ type: 'PaymentMethods', id }],
      transformResponse: (response) => {
        const pm = response.data;
        if (!pm) return null;

        return {
          ...pm,
          expirationDate: formatCardExpiration(pm.card?.expMonth, pm.card?.expYear),
          cardholderName: pm.billingDetails?.name || 'N/A',
        };
      },
    }),

    // Create a payment method (after Setup Intent confirmation)
    createPaymentMethod: builder.mutation({
      query: (payload) => ({
        url: 'stripe/payment-methods',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['PaymentMethods'],
    }),

    // Update a payment method
    updatePaymentMethod: builder.mutation({
      query: ({ paymentMethodId, updates }) => ({
        url: `stripe/payment-methods/${paymentMethodId}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: ['PaymentMethods'],
    }),

    // Set a payment method as default
    setDefaultPaymentMethod: builder.mutation({
      query: (paymentMethodId) => ({
        url: `stripe/payment-methods/${paymentMethodId}/default`,
        method: 'POST',
      }),
      invalidatesTags: ['PaymentMethods'],
    }),

    // Delete a payment method
    deletePaymentMethod: builder.mutation({
      query: (paymentMethodId) => ({
        url: `stripe/payment-methods/${paymentMethodId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PaymentMethods'],
    }),

    // Sync payment methods from Stripe
    syncPaymentMethods: builder.mutation({
      query: (stripeCustomerId) => ({
        url: 'stripe/payment-methods/sync',
        method: 'POST',
        body: { stripeCustomerId },
      }),
      invalidatesTags: ['PaymentMethods'],
    }),

    // ============= Invoices =============

    // Get paginated invoices (Stripe billing history)
    getInvoices: builder.query({
      query: ({ limit = 10, startingAfter, endingBefore } = {}) => {
        const params = new URLSearchParams();

        params.append('limit', limit.toString());

        if (startingAfter) {
          params.append('starting_after', startingAfter);
        }

        if (endingBefore) {
          params.append('ending_before', endingBefore);
        }

        return `stripe/invoices?${params.toString()}`;
      },
      providesTags: ['Invoices'],
    }),

    // Get a specific invoice
    getInvoiceById: builder.query({
      query: (invoiceId) => `stripe/invoices/${invoiceId}`,
      providesTags: (result, error, invoiceId) => [{ type: 'Invoices', id: invoiceId }],
      transformResponse: (response) => response.data,
    }),
  }),
});

export const {
  useCreateSetupIntentMutation,
  useConfirmSetupIntentMutation,
  useGetPaymentMethodsQuery,
  useLazyGetPaymentMethodsQuery,
  useGetDefaultPaymentMethodQuery,
  useGetPaymentMethodByIdQuery,
  useCreatePaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
  useDeletePaymentMethodMutation,
  useSyncPaymentMethodsMutation,
  // Invoices
  useGetInvoicesQuery,
  useLazyGetInvoicesQuery,
  useGetInvoiceByIdQuery,
} = stripeApi;
